import React, { useState, useEffect } from 'react';
import { teamsAPI, stadiaAPI, getBestTeamLogo } from '../../services/api';
import { parseMisc } from '../../utils/imageUtils';
import UploadImagePreview from '../common/UploadImagePreview';

const LOGO_PREVIEW_CLASS = {
  logoBig: 'w-20 h-20 object-contain border rounded bg-white',
  logoSmall: 'w-16 h-16 object-contain border rounded bg-white',
  dress: 'w-16 h-16 object-contain border rounded bg-white',
};

const TeamLogoCell = ({ misc, teamName, size = 'sm' }) => {
  const logoPath = getBestTeamLogo(misc);
  const boxClass = size === 'lg' ? 'w-16 h-16' : 'w-12 h-12';
  const imgClass = `${boxClass} rounded object-contain bg-white`;

  if (!logoPath) {
    return (
      <div className={`${boxClass} bg-gray-200 rounded flex items-center justify-center text-gray-500 text-xs`}>
        No Logo
      </div>
    );
  }

  return (
    <div className="avatar">
      <div className={`${boxClass} rounded`}>
        <UploadImagePreview
          path={logoPath}
          alt={`${teamName} logo`}
          className={imgClass}
          fallback={(
            <div className={`${boxClass} bg-gray-200 rounded flex items-center justify-center text-gray-500 text-xs`}>
              No Logo
            </div>
          )}
        />
      </div>
    </div>
  );
};

const LogoUploadField = ({ label, field, path, selectedFile, onFileChange }) => (
  <div className="form-control">
    <label className="label">
      <span className="label-text">{label}</span>
    </label>
    <input
      type="file"
      accept="image/*"
      className="file-input file-input-bordered file-input-sm"
      onChange={(e) => onFileChange(field, e.target.files[0])}
    />
    {(path || selectedFile) && (
      <div className="mt-2">
        <UploadImagePreview
          path={path}
          selectedFile={selectedFile}
          alt={`${label} preview`}
          className={LOGO_PREVIEW_CLASS[field]}
          fallback={(
            <div className="text-xs text-gray-400 mt-1">No preview available</div>
          )}
        />
      </div>
    )}
  </div>
);

const TeamsSettings = () => {
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [stadia, setStadia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    published: true,
    start: 0,
    stid: '',
    misc: {
      address: '',
      telephone: '',
      homepage: '',
      logoBig: '',
      logoSmall: '',
      dress: ''
    }
  });
  const [selectedFiles, setSelectedFiles] = useState({
    logoBig: null,
    logoSmall: null,
    dress: null
  });  useEffect(() => {
    fetchTeams();
    fetchStadia();
  }, []);

  useEffect(() => {
    // Filter teams based on search term
    if (!searchTerm.trim()) {
      setFilteredTeams(teams);
    } else {
      const filtered = teams.filter(team => {
        const searchLower = searchTerm.toLowerCase();
        return (
          team.name.toLowerCase().includes(searchLower) ||
          (team.misc?.address && team.misc.address.toLowerCase().includes(searchLower)) ||
          (team.stid?.name && team.stid.name.toLowerCase().includes(searchLower)) ||
          (team.misc?.telephone && team.misc.telephone.toLowerCase().includes(searchLower))
        );
      });
      setFilteredTeams(filtered);
    }
  }, [teams, searchTerm]);  const fetchTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
      setError('Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };  const fetchStadia = async () => {
    try {
      const response = await stadiaAPI.getAll();
      setStadia(response.data);
    } catch (err) {
      console.error('Failed to fetch stadia');
    }
  };const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);    try {
      let updatedFormData = { ...formData };
      
      // Handle file uploads if any files are selected
      if (selectedFiles.logoBig || selectedFiles.logoSmall || selectedFiles.dress) {
        const uploadFormData = new FormData();
        let hasFiles = false;
        
        if (selectedFiles.logoBig) {
          uploadFormData.append('logoBig', selectedFiles.logoBig);
          hasFiles = true;
        }
        if (selectedFiles.logoSmall) {
          uploadFormData.append('logoSmall', selectedFiles.logoSmall);
          hasFiles = true;
        }
        if (selectedFiles.dress) {
          uploadFormData.append('dress', selectedFiles.dress);
          hasFiles = true;
        }
        
        if (hasFiles) {
          console.log('Uploading files...');
          const uploadResponse = await teamsAPI.uploadFiles(uploadFormData);
          console.log('Upload response:', uploadResponse.data);
          
          // Update the misc object with the uploaded filenames
          if (uploadResponse.data.files) {
            updatedFormData.misc = {
              ...updatedFormData.misc,
              ...uploadResponse.data.files
            };
          }
        }
      }
        let response;
      if (editingTeam) {
        response = await teamsAPI.update(editingTeam._id, updatedFormData);
      } else {
        response = await teamsAPI.create(updatedFormData);
      }
      
      setShowModal(false);
      setEditingTeam(null);
      setFormData({ 
        name: '', 
        published: true, 
        start: 0, 
        stid: '', 
        misc: {
          address: '',
          telephone: '',
          homepage: '',
          logoBig: '',
          logoSmall: '',
          dress: ''
        }      });
      setSelectedFiles({ logoBig: null, logoSmall: null, dress: null });
      
      await fetchTeams();
      
    } catch (err) {
      console.error('Error saving team:', err);
      setError('Failed to save team');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);    setFormData({
      name: team.name,
      published: team.published,
      start: team.start || 0,
      stid: team.stid?._id || '',
      misc: {
        address: parseMisc(team.misc)?.address || '',
        telephone: parseMisc(team.misc)?.telephone || '',
        homepage: parseMisc(team.misc)?.homepage || '',
        logoBig: parseMisc(team.misc)?.logoBig || '',
        logoSmall: parseMisc(team.misc)?.logoSmall || '',
        dress: parseMisc(team.misc)?.dress || ''
      }
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      try {
        await teamsAPI.delete(id);
        fetchTeams();
      } catch (err) {
        setError('Failed to delete team');
      }
    }
  };  const openModal = () => {
    setEditingTeam(null);
    setFormData({ 
      name: '', 
      published: true, 
      start: 0, 
      stid: '', 
      misc: {
        address: '',
        telephone: '',
        homepage: '',
        logoBig: '',
        logoSmall: '',
        dress: ''
      }
    });
    setSelectedFiles({ logoBig: null, logoSmall: null, dress: null });
    setShowModal(true);
  };
  const handleFileChange = (field, file) => {
    setSelectedFiles(prev => ({
      ...prev,
      [field]: file
    }));  };

  if (loading) return <div className="loading loading-spinner loading-lg"></div>;

  return (    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Teams Management</h2>
        <button onClick={openModal} className="btn btn-primary">
          Add New Team
        </button>
      </div>

      {/* Search/Filter Section */}
      <div className="flex gap-4 items-center">
        <div className="form-control flex-1 max-w-md">
          <div className="input-group">
            <span>🔍</span>
            <input
              type="text"
              placeholder="Search teams by name, address, stadium..."
              className="input input-bordered w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {searchTerm && (
          <div className="text-sm text-gray-500">
            {filteredTeams.length} of {teams.length} teams
          </div>
        )}
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="btn btn-ghost btn-sm"
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>      )}

      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>Logo</th>
              <th>Name</th>
              <th>Address</th>
              <th>Stadium</th>
              <th>Start Points</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(filteredTeams) && filteredTeams.length > 0 ? (
              filteredTeams.map((team) => (
                <tr key={team._id}>
                  <td className="w-16">
                    <TeamLogoCell misc={team.misc} teamName={team.name} />
                  </td>
                  <td>{team.name}</td>
                  <td>
                    {team.misc?.address ? (
                      <span className="text-sm">{team.misc.address}</span>
                    ) : (
                      <span className="text-gray-500">No Address</span>
                    )}
                  </td>
                  <td>
                    {team.stid ? (
                      <div className="badge badge-outline">
                        {team.stid.name || 'Stadium Assigned'}
                      </div>
                    ) : (
                      <span className="text-gray-500">No Stadium</span>
                    )}
                  </td>
                  <td>
                    {team.start === 0 ? (
                      <span className="text-gray-500">Normal (0)</span>
                    ) : team.start < 0 ? (
                      <span className="text-red-500 font-medium">{team.start} pts</span>
                    ) : (
                      <span className="text-green-500 font-medium">+{team.start} pts</span>
                    )}
                  </td>
                  <td>
                    <div className={`badge ${team.published ? 'badge-success' : 'badge-error'}`}>
                      {team.published ? 'Active' : 'Inactive'}
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleEdit(team)}
                        className="btn btn-sm btn-ghost"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(team._id)}
                        className="btn btn-sm btn-error"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (              <tr>
                <td colSpan="7" className="text-center text-gray-500 py-8">
                  {searchTerm ? 'No teams found matching your search.' : 'No teams available.'}
                </td>
              </tr>
            )}
          </tbody>        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Array.isArray(filteredTeams) && filteredTeams.length > 0 ? (
          filteredTeams.map((team) => (
            <div key={team._id} className="modern-card p-6">
              {/* Team Header */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0">
                  <TeamLogoCell misc={team.misc} teamName={team.name} size="lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-800 dark:text-white">
                    {team.name}
                  </h3>
                  <div className={`badge ${team.published ? 'badge-success' : 'badge-error'} badge-sm`}>
                    {team.published ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>

              {/* Team Details */}
              <div className="space-y-3 mb-4">
                {team.misc?.address && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 text-sm">📍</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{team.misc.address}</span>
                  </div>
                )}
                
                {team.stid?.name && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">🏟️</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{team.stid.name}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">⭐</span>
                  <span className="text-sm">
                    {team.start === 0 ? (
                      <span className="text-gray-500">Normal (0)</span>
                    ) : team.start < 0 ? (
                      <span className="text-red-500 font-medium">{team.start} pts</span>
                    ) : (
                      <span className="text-green-500 font-medium">+{team.start} pts</span>
                    )}
                  </span>
                </div>

                {team.misc?.telephone && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm">📞</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{team.misc.telephone}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button 
                  onClick={() => handleEdit(team)}
                  className="btn btn-sm btn-primary flex-1"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDelete(team._id)}
                  className="btn btn-sm btn-error flex-1"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-500 py-8">
            {searchTerm ? 'No teams found matching your search.' : 'No teams available.'}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">
              {editingTeam ? 'Edit Team' : 'Add New Team'}
            </h3>            <form onSubmit={handleSubmit} className="space-y-4 mt-4">              {/* Logo Uploads Section */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <LogoUploadField
                  label="Logo Big"
                  field="logoBig"
                  path={formData.misc.logoBig}
                  selectedFile={selectedFiles.logoBig}
                  onFileChange={handleFileChange}
                />
                <LogoUploadField
                  label="Logo Small"
                  field="logoSmall"
                  path={formData.misc.logoSmall}
                  selectedFile={selectedFiles.logoSmall}
                  onFileChange={handleFileChange}
                />
                <LogoUploadField
                  label="Dress"
                  field="dress"
                  path={formData.misc.dress}
                  selectedFile={selectedFiles.dress}
                  onFileChange={handleFileChange}
                />
              </div>

              {/* Team Details Section */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Name</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="input input-bordered"
                    required
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Address</span>
                  </label>
                  <input
                    type="text"
                    value={formData.misc.address}
                    onChange={(e) => setFormData({
                      ...formData, 
                      misc: { ...formData.misc, address: e.target.value }
                    })}
                    className="input input-bordered"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Telephone</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.misc.telephone}
                    onChange={(e) => setFormData({
                      ...formData, 
                      misc: { ...formData.misc, telephone: e.target.value }
                    })}
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Homepage</span>
                  </label>
                  <input
                    type="url"
                    value={formData.misc.homepage}
                    onChange={(e) => setFormData({
                      ...formData, 
                      misc: { ...formData.misc, homepage: e.target.value }
                    })}
                    className="input input-bordered"
                    placeholder="https://"
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Standard Stadium</span>
                </label>
                <select
                  value={formData.stid}
                  onChange={(e) => setFormData({...formData, stid: e.target.value})}
                  className="select select-bordered"
                >
                  <option value="">Select Stadium</option>
                  {Array.isArray(stadia) && stadia.map((stadium) => (
                    <option key={stadium._id} value={stadium._id}>
                      {stadium.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Starting Points</span>
                  </label>
                  <input
                    type="number"
                    value={formData.start}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData, 
                        start: value === '' ? 0 : parseInt(value, 10)
                      });
                    }}
                    className="input input-bordered"
                    min="-50"
                    max="50"
                    placeholder="0 = normal start, negative = penalty points"
                  />
                  <label className="label">
                    <span className="label-text-alt">0 = normal start, negative values = penalty points</span>
                  </label>
                </div>

                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text">Active</span>
                    <input
                      type="checkbox"
                      checked={formData.published}
                      onChange={(e) => setFormData({...formData, published: e.target.checked})}
                      className="checkbox"
                    />
                  </label>
                </div>
              </div>              <div className="modal-action">
                <button 
                  type="submit" 
                  className={`btn btn-primary ${uploading ? 'loading' : ''}`}
                  disabled={uploading}
                >
                  {uploading ? 'Saving...' : (editingTeam ? 'Update Team' : 'Create Team')}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)}
                  className="btn"
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default TeamsSettings;
